import React from "react";
import { isMobile } from "react-device-detect";
import { motion } from "framer-motion";
import classNames from "classnames";
import Button from "../Button";
import { ArenaReward } from "../../types/pages/MarketPlace";
import { useAppTranslation } from "../../i18n/useAppTranslation";

interface CardAction {
  icon?: string;
  text: string;
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
}

interface CardBadge {
  text: string;
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  icon?: string;
  background?: string;
}

interface CardTimer {
  time: Date;
  title: string;
}

interface ExpandableCardProps extends ArenaReward {
  title: string;
  image: string;
  description: string;
  shortDescription?: string;
  level?: number;
  badges?: CardBadge[];
  timer?: CardTimer;
  action?: CardAction;
  className?: string;
  style?: React.CSSProperties;
  LevelBadgeComponent?: React.ComponentType<any>;
  onExpand?: (id: number | string) => void;
  isExpanded:boolean
}

const ExpandableCard: React.FC<ExpandableCardProps> = ({
  id,
  title,
  image,
  description,
  shortDescription,
  isPackage = false,
  level,
  badges = [],
  timer,
  action,
  className = "",
  style = {},
  LevelBadgeComponent,
  onExpand,
  isExpanded,
}) => {
  const { t } = useAppTranslation(["common", "village"]);

  const toggleExpansion = () => {
    if (onExpand) {
      onExpand(id);
    }
  };

  return (
    <div
      style={{
        minHeight: isMobile ? "auto" : 300,
        cursor: "pointer",
        zIndex: isExpanded ? 101 : 1,
        position: isExpanded ? "relative" : "static",
        transform: isExpanded ? isMobile?"scale(1.03)": "scale(1.1)" : "scale(1)",
        transition: "transform 0.3s ease",
        ...style,
      }}
      className={classNames(
        `land-box py-4 d-flex flex-column justify-content-between`,
        {
          "expanded-card": isExpanded,
          "land-package": isPackage,
        },
        className
      )}
    >
      {/* Timer */}
      {timer && (
        <div
          style={{
            top: "32%",
            left: 8,
            borderRadius: "0 10px 10px 0",
            background: "rgba(0,0,0,0.2)",
          }}
          className="position-absolute w-fit p-1 d-flex align-items-center justify-content-center pt-1 light-yellow fs-med"
        >
          <div className="d-flex flex-column align-items-center justify-content-center fs-small">
            <span className="fs-small bold">{timer.title}</span>
            <span>
              {Math.floor((timer.time.getTime() - Date.now()) / (1000 * 60 * 60 * 24))}
              <span className="fs-tiny">{t("village:time.short.days")}</span>
            </span>
            <span>
              {Math.floor(((timer.time.getTime() - Date.now()) % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))}
              <span className="fs-tiny">{t("village:time.short.hours")}</span>
            </span>
            <span>
              {Math.floor(((timer.time.getTime() - Date.now()) % (1000 * 60 * 60)) / (1000 * 60))}
              <span className="fs-tiny">{t("village:time.short.minutes")}</span>
            </span>
            <span>
              {Math.floor(((timer.time.getTime() - Date.now()) % (1000 * 60)) / 1000)}
              <span className="fs-tiny">{t("common:time.seconds").charAt(0)}</span>
            </span>
          </div>
        </div>
      )}

      {/* Badges */}
      {badges.map((badge, index) => (
        <div
          key={index}
          className="position-absolute ps-1 d-flex align-items-center justify-content-center pt-1 light-yellow fs-med bold"
          style={{
            top: badge.position?.includes("top") ? -5 : undefined,
            bottom: badge.position?.includes("bottom") ? -5 : undefined,
            left: badge.position?.includes("left") ? 10 : undefined,
            right: badge.position?.includes("right") ? 10 : undefined,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            width: 77,
            height: 44,
            backgroundImage: badge.background || "url('/assets/images/badges/shop-badge.png')",
          }}
        >
          {badge.text}
        </div>
      ))}

      {/* Level Badge */}
      {level && LevelBadgeComponent && (
        <div
          style={{
            width: 35,
            height: 35,
            right: isMobile ? 3 : "13px",
            top: "5px",
            marginInline: "0px !important",
          }}
          className="position-absolute"
        >
          <LevelBadgeComponent
            level={level}
            style={{ zIndex: 10 }}
            className="position-absolute fs-big bold mx-0"
          />
        </div>
      )}

      {/* Card Body */}
      <div
        style={{ zIndex: 4 }}
        className="lb-body mt-2 position-relative d-flex flex-column align-items-center justify-content-around flex-1"
        onClick={toggleExpansion}
      >
        <div
          style={{ fontSize: isMobile ? 18 : 26, maxWidth: "100%" }}
          className="light-yellow px-2 text-truncate mb-2"
        >
          {title}
        </div>
        {/* Fixed-height frame so every card keeps the same body height: item art
            is 256px while troop icons are 128px, and letting each image set its
            own height left the rows ragged and the items oversized. */}
        <div
          className="image"
          style={{ height: isPackage ? undefined : isMobile ? 90 : 130 }}
        >
          <img
            style={{
              maxHeight: isPackage ? (isMobile ? 300 : 350) : "100%",
              maxWidth: "100%",
              objectFit: "contain",
            }}
            src={image}
            alt={title}
          />
        </div>

        {/* Description with conditional classes based on expansion state */}
        {!isExpanded && (
          <div
            className={`text-center px-3 light-yellow collapsed-description`}
            dangerouslySetInnerHTML={{
              __html: shortDescription || description,
            }}
          ></div>
        )}
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            style={{ maxWidth: "90%" }}
            className="text-center mx-auto fs-med light-yellow expanded-description"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </div>

      {/* Action Button */}
      {action && (
        <div className="lb-button d-flex justify-content-center">
          <Button
            size={isMobile ? "small" : "medium"}
            disabled={action.disabled}
            onClick={(e) => {
              e.stopPropagation(); // Prevent card expansion when clicking the button
              action.onClick(e);
            }}
            className="bg-100 gap-2 px-3"
            variant="default"
          >
            {action.icon && (
              <img
                src={action.icon}
                width={isMobile ? 17 : 25}
                height={"auto"}
                alt=""
              />
            )}
            {action.text}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ExpandableCard;
